import { Json } from "./json";

/**
 * Normalizes serialization artifacts (tagged wire values like `{"$$date": …}`
 * and hydrated class instances) to plain JSON. Never throws.
 */
export function normalizeWireArtifacts(value: unknown): unknown {
  if (value === undefined) {
    return null;
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeWireArtifacts);
  }
  const toJson = (value as { toJSON?: unknown }).toJSON;
  if (typeof toJson === "function") {
    return normalizeWireArtifacts((toJson as () => unknown).call(value));
  }
  const entries = Object.entries(value);
  if (entries.length === 1) {
    const [key, tagged] = entries[0]!;
    if (
      key === "$$date" &&
      (typeof tagged === "string" || typeof tagged === "number")
    ) {
      const date = new Date(tagged);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    }
    if (key === "$$undefined") {
      return null;
    }
  }
  return Object.fromEntries(
    entries
      .filter(([, entryValue]) => entryValue !== undefined)
      .map(([key, entryValue]) => [key, normalizeWireArtifacts(entryValue)]),
  );
}

const MAX_REPORTED_PATHS = 25;

export interface PlainJsonNormalization {
  value: Json;
  /** JSONPath-style rewrite locations, capped at {@link MAX_REPORTED_PATHS}. */
  changedPaths: string[];
  /** Total rewrites, including any beyond the reporting cap. */
  changeCount: number;
}

/**
 * Write-side guard variant of {@link normalizeWireArtifacts}: same
 * normalization, but reports every rewritten path so violations can be logged.
 */
export function normalizeToPlainJson(value: unknown): PlainJsonNormalization {
  const changedPaths: string[] = [];
  let changeCount = 0;
  const change = (path: string) => {
    changeCount += 1;
    if (changedPaths.length < MAX_REPORTED_PATHS) {
      changedPaths.push(path);
    }
  };

  const normalizeTag = (
    key: string,
    tagged: unknown,
    path: string,
  ): { value: Json } | undefined => {
    switch (key) {
      case "$$date": {
        if (typeof tagged !== "string" && typeof tagged !== "number") {
          return undefined;
        }
        const date = new Date(tagged);
        return {
          value: Number.isNaN(date.getTime()) ? null : date.toISOString(),
        };
      }
      case "$$undefined":
        return { value: null };
      case "$$bigint":
      case "$$big":
        return typeof tagged === "string" || typeof tagged === "number"
          ? { value: String(tagged) }
          : undefined;
      case "$$instant": {
        if (typeof tagged !== "number") {
          return undefined;
        }
        const instant = new Date(tagged);
        return {
          value: Number.isNaN(instant.getTime()) ? null : instant.toISOString(),
        };
      }
      case "$$plaindate":
      case "$$plaintime":
        return typeof tagged === "string" ? { value: tagged } : undefined;
      case "$$map":
        return Array.isArray(tagged)
          ? { value: normalize(tagged, path) }
          : undefined;
      default:
        return undefined;
    }
  };

  const normalize = (value: unknown, path: string): Json => {
    if (value === undefined) {
      change(path);
      return null;
    }
    if (typeof value === "bigint") {
      change(path);
      return value.toString();
    }
    if (typeof value === "function" || typeof value === "symbol") {
      change(path);
      return null;
    }
    if (value === null || typeof value !== "object") {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((element, index) =>
        normalize(element, `${path}[${index}]`),
      );
    }
    if (value instanceof Map) {
      change(path);
      return [...value.entries()].map(([entryKey, entryValue], index) => [
        normalize(entryKey, `${path}[${index}][0]`),
        normalize(entryValue, `${path}[${index}][1]`),
      ]);
    }
    if (value instanceof Set) {
      change(path);
      return [...value].map((element, index) =>
        normalize(element, `${path}[${index}]`),
      );
    }
    const toJson = (value as { toJSON?: unknown }).toJSON;
    if (typeof toJson === "function") {
      change(path);
      return normalize((toJson as () => unknown).call(value), path);
    }
    const entries = Object.entries(value);
    if (entries.length === 1) {
      const [key, tagged] = entries[0]!;
      const normalizedTag = normalizeTag(key, tagged, path);
      if (normalizedTag) {
        change(path);
        return normalizedTag.value;
      }
    }
    return Object.fromEntries(
      entries.flatMap(([key, entryValue]): [string, Json][] => {
        if (entryValue === undefined) {
          change(`${path}.${key}`);
          return [];
        }
        return [[key, normalize(entryValue, `${path}.${key}`)]];
      }),
    );
  };

  return { value: normalize(value, "$"), changedPaths, changeCount };
}
